import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, MapPin, Star, Loader2, ChevronRight,
  Hotel, Utensils, Coffee, Wine, Landmark, Building2,
  TreePine, Umbrella, ShoppingBag, Sparkles, Music, Ticket,
  Navigation, X, ExternalLink, Heart, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import debounce from 'lodash/debounce';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  hotels: Hotel,
  restaurants: Utensils,
  cafes: Coffee,
  bars: Wine,
  attractions: Landmark,
  museums: Building2,
  parks: TreePine,
  beaches: Umbrella,
  shopping: ShoppingBag,
  spas: Sparkles,
  nightlife: Music,
  entertainment: Ticket,
};

// Price level display
const PRICE_LEVELS = {
  1: '$',
  2: '$$',
  3: '$$$',
  4: '$$$$'
};

export default function PlacesSearch({ 
  onPlaceSelect, 
  initialLocation = '',
  initialCategory = '',
  compact = false 
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState(initialLocation);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [dataSource, setDataSource] = useState(null);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Search when location or category changes
  useEffect(() => {
    if (location) {
      searchPlaces();
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/places/categories`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const searchPlaces = async () => {
    if (!location) {
      toast.error('Please enter a location');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        near: location,
        limit: '20'
      });
      
      if (searchQuery) {
        params.append('query', searchQuery);
      }
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const response = await axios.get(`${API_URL}/api/places/search?${params.toString()}`);
      const data = response.data;
      
      setResults(data.results || []);
      setDataSource(data.source);
      
      if (data.source === 'demo') {
        // Show info about demo data
        toast.info('Showing curated places for this destination');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to search places');
      toast.error('Failed to search places');
    } finally {
      setLoading(false);
    }
  };

  // Debounced autocomplete
  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/places/autocomplete?query=${encodeURIComponent(query)}&limit=5`);
        setSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Autocomplete error:', err);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchSuggestions(value);
  };

  const selectSuggestion = (suggestion) => {
    if (suggestion.type === 'place') {
      setSearchQuery(suggestion.name);
      if (onPlaceSelect) {
        onPlaceSelect(suggestion);
      }
    } else if (suggestion.type === 'location') {
      setLocation(suggestion.name);
    }
    setShowSuggestions(false);
    searchPlaces();
  };

  const toggleFavorite = (placeId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(placeId)) {
        newFavorites.delete(placeId);
      } else {
        newFavorites.add(placeId);
      }
      return newFavorites;
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchPlaces();
    }
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || MapPin;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="w-full" data-testid="places-search">
      {/* Search Header */}
      <div className={`space-y-4 ${compact ? 'mb-4' : 'mb-8'}`}>
        {/* Location Input */}
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter destination (e.g., Dubai, Paris, New York)"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                data-testid="location-input"
              />
            </div>
            <button
              onClick={searchPlaces}
              disabled={loading || !location}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-medium flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="search-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">Explore</span>
            </button>
          </div>
        </div>

        {/* Search Query Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Search hotels, restaurants, attractions..."
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            data-testid="search-query-input"
          />
          
          {/* Autocomplete Suggestions */}
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-[var(--bg-card)] border border-[var(--border-primary)] rounded-xl shadow-lg overflow-hidden z-50"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => selectSuggestion(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-[var(--bg-secondary)] flex items-center gap-3 transition-colors"
                  >
                    {suggestion.type === 'place' ? (
                      <MapPin className="w-4 h-4 text-[var(--color-primary)]" />
                    ) : (
                      <Navigation className="w-4 h-4 text-[var(--color-secondary)]" />
                    )}
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">{suggestion.name}</p>
                      {suggestion.categories && (
                        <p className="text-xs text-[var(--text-secondary)]">
                          {suggestion.categories.join(', ')}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Category Pills */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory
                  ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white'
                  : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-primary)]'
              }`}
            >
              All
            </button>
            {categories.slice(0, compact ? 5 : 12).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white'
                    : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-primary)]'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <CategoryIcon category={cat.id} />
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {error && (
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
          <span className="ml-3 text-[var(--text-secondary)]">Searching places...</span>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-4">
          {/* Results Header */}
          <div className="flex items-center justify-between">
            <p className="text-[var(--text-secondary)]">
              {results.length} places found {location && `in ${location}`}
            </p>
            {dataSource === 'demo' && (
              <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs">
                Curated Selection
              </span>
            )}
          </div>

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((place, index) => (
              <motion.div
                key={place.fsq_id || place.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative bg-[var(--bg-card)] rounded-2xl overflow-hidden border border-[var(--border-primary)] hover:border-[var(--color-primary)]/50 transition-all cursor-pointer"
                onClick={() => onPlaceSelect && onPlaceSelect(place)}
                data-testid={`place-card-${place.fsq_id || place.id}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={place.image || place.photos?.[0]?.url || `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80`}
                    alt={place.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(place.fsq_id || place.id);
                    }}
                    className="absolute top-3 right-3 p-2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
                  >
                    <Heart 
                      className={`w-5 h-5 ${
                        favorites.has(place.fsq_id || place.id)
                          ? 'text-red-500 fill-red-500'
                          : 'text-white'
                      }`}
                    />
                  </button>

                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[var(--color-primary)]/80 backdrop-blur-sm text-white text-xs font-medium">
                    {place.category || place.categories?.[0] || 'Place'}
                  </div>

                  {/* Rating */}
                  {place.rating && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span>{typeof place.rating === 'number' ? place.rating.toFixed(1) : place.rating}</span>
                    </div>
                  )}

                  {/* Price */}
                  {place.price && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-[var(--color-gold)] text-sm font-medium">
                      {PRICE_LEVELS[place.price] || place.price}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--color-primary)] transition-colors line-clamp-1">
                    {place.name}
                  </h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-1">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {place.address || `${place.locality || ''}, ${place.country || ''}`}
                  </p>
                  
                  {/* Additional Info */}
                  {place.distance && (
                    <p className="text-xs text-[var(--text-secondary)] mt-2">
                      {(place.distance / 1000).toFixed(1)} km away
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && results.length === 0 && location && (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 mx-auto text-[var(--text-secondary)] mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No places found</h3>
          <p className="text-[var(--text-secondary)]">Try adjusting your search or exploring a different area</p>
        </div>
      )}
    </div>
  );
}
